import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit2, Check, X, ChevronRight, BookOpen, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { treatmentSchema } from "@/lib/validation";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Treatment {
  id: string;
  symptom_keywords: string[];
  treatment_type: string;
  title: string;
  detail: string;
  posology?: string;
  contraindications: string[];
  priority: number;
  is_active: boolean;
}

const treatmentTypes = [
  { value: "nutrition", label: "Nutrition" },
  { value: "phytotherapie", label: "Phytothérapie" },
  { value: "hygiene_vie", label: "Hygiène de vie" },
  { value: "complement", label: "Complément" },
  { value: "technique_bien_etre", label: "Technique bien-être" },
];

const subLibraries = [
  { path: "/clay-library", label: "Argile", icon: "🌍", description: "Cataplasmes et usage interne" },
  { path: "/aromatherapy-library", label: "Huiles Essentielles", icon: "🌸", description: "Protocoles aromatiques" },
  { path: "/gemmotherapy-library", label: "Gemmothérapie", icon: "🌱", description: "Macérats de bourgeons" },
  { path: "/vegetable-oils-library", label: "Huiles Végétales", icon: "🫒", description: "Huiles de soin" },
  { path: "/hydrolats-library", label: "Hydrolats", icon: "💧", description: "Eaux florales et hydrosols" },
  { path: "/apitherapy-library", label: "Apithérapie", icon: "🐝", description: "Produits de la ruche" },
];

export default function TreatmentsLibrary() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    symptom_keywords: "",
    treatment_type: "nutrition",
    title: "",
    detail: "",
    posology: "",
    contraindications: "",
    priority: 2,
  });

  useEffect(() => {
    loadTreatments();
  }, []);

  const loadTreatments = async () => {
    try {
      const { data, error } = await supabase
        .from("practitioner_treatments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTreatments(data || []);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erreur",
          description: "Vous devez être connecté",
          variant: "destructive",
        });
        return;
      }

      const keywords = formData.symptom_keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);
      const contraindications = formData.contraindications
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c);

      const treatmentData = {
        symptom_keywords: keywords,
        treatment_type: formData.treatment_type,
        title: formData.title,
        detail: formData.detail,
        posology: formData.posology || "",
        contraindications,
        priority: formData.priority,
      };

      // Validate treatment data
      const validation = treatmentSchema.safeParse(treatmentData);
      if (!validation.success) {
        const errors = validation.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`).join(", ");
        toast({
          title: "Validation échouée",
          description: errors,
          variant: "destructive",
        });
        return;
      }

      const finalData = {
        ...treatmentData,
        posology: treatmentData.posology || null,
        is_active: true,
      };

      if (editingId) {
        const { error } = await supabase.from("practitioner_treatments").update(finalData).eq("id", editingId);

        if (error) throw error;
        toast({ title: "Traitement modifié avec succès" });
      } else {
        const { error } = await supabase.from("practitioner_treatments").insert([{ ...finalData, user_id: user.id }]);

        if (error) throw error;
        toast({ title: "Traitement ajouté avec succès" });
      }

      resetForm();
      loadTreatments();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (treatment: Treatment) => {
    setFormData({
      symptom_keywords: treatment.symptom_keywords.join(", "),
      treatment_type: treatment.treatment_type,
      title: treatment.title,
      detail: treatment.detail,
      posology: treatment.posology || "",
      contraindications: treatment.contraindications.join(", "),
      priority: treatment.priority,
    });
    setEditingId(treatment.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce traitement ?")) return;

    try {
      const { error } = await supabase.from("practitioner_treatments").delete().eq("id", id);

      if (error) throw error;
      toast({ title: "Traitement supprimé" });
      loadTreatments();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      symptom_keywords: "",
      treatment_type: "nutrition",
      title: "",
      detail: "",
      posology: "",
      contraindications: "",
      priority: 2,
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-primary">Chargement...</div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Bibliothèque de Traitements"
        subtitle="Gérez vos traitements personnalisés et accédez aux ressources naturopathiques"
        icon={<BookOpen className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
      />

      <div className="space-y-6">

        {/* Sub-libraries navigation */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">📚 Bibliothèques spécialisées</CardTitle>
            <CardDescription>Accédez aux ressources naturopathiques par catégorie</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {subLibraries.map((lib) => (
                <Button
                  key={lib.path}
                  variant="outline"
                  className="h-auto py-3 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 hover:bg-primary/5"
                  onClick={() => navigate(lib.path)}
                >
                  <span className="text-xl sm:text-2xl">{lib.icon}</span>
                  <span className="font-semibold text-xs sm:text-sm text-center leading-tight">{lib.label}</span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground text-center hidden sm:block">{lib.description}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Mes traitements personnalisés</h2>
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? <X className="mr-2 w-4 h-4" /> : <Plus className="mr-2 w-4 h-4" />}
            {showForm ? "Annuler" : "Nouveau traitement"}
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{editingId ? "Modifier le traitement" : "Nouveau traitement"}</CardTitle>
              <CardDescription>Ajoutez des traitements qui seront suggérés lors des analyses</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="symptom_keywords">Mots-clés des symptômes (séparés par des virgules)</Label>
                  <Input
                    id="symptom_keywords"
                    value={formData.symptom_keywords}
                    onChange={(e) => setFormData({ ...formData, symptom_keywords: e.target.value })}
                    placeholder="fatigue, stress, insomnie"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="treatment_type">Type de traitement</Label>
                  <Select
                    value={formData.treatment_type}
                    onValueChange={(value) => setFormData({ ...formData, treatment_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {treatmentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="title">Titre du traitement</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Infusion de camomille"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="detail">Détails</Label>
                  <Textarea
                    id="detail"
                    value={formData.detail}
                    onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                    placeholder="Description détaillée du traitement"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="posology">Posologie (optionnel)</Label>
                  <Input
                    id="posology"
                    value={formData.posology}
                    onChange={(e) => setFormData({ ...formData, posology: e.target.value })}
                    placeholder="Ex: 1 tasse 3 fois par jour"
                  />
                </div>

                <div>
                  <Label htmlFor="contraindications">Contre-indications (séparées par des virgules)</Label>
                  <Input
                    id="contraindications"
                    value={formData.contraindications}
                    onChange={(e) => setFormData({ ...formData, contraindications: e.target.value })}
                    placeholder="Grossesse, allaitement"
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priorité (1 = haute, 3 = basse)</Label>
                  <Select
                    value={formData.priority.toString()}
                    onValueChange={(value) => setFormData({ ...formData, priority: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Haute priorité</SelectItem>
                      <SelectItem value="2">Priorité moyenne</SelectItem>
                      <SelectItem value="3">Priorité basse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button type="submit">
                    <Check className="mr-2 w-4 h-4" />
                    {editingId ? "Modifier" : "Ajouter"}
                  </Button>
                  {editingId && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Annuler
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {treatments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Aucun traitement dans votre bibliothèque. Commencez par en ajouter un !
              </CardContent>
            </Card>
          ) : (
            treatments.map((treatment) => (
              <Card key={treatment.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        {treatment.title}
                        <Badge variant={treatment.priority === 1 ? "default" : "secondary"}>
                          Priorité {treatment.priority}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        {treatmentTypes.find((t) => t.value === treatment.treatment_type)?.label}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="icon" onClick={() => handleEdit(treatment)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => handleDelete(treatment.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="font-semibold">Symptômes ciblés: </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {treatment.symptom_keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="outline">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="font-semibold">Détails: </span>
                    <p className="text-muted-foreground mt-1">{treatment.detail}</p>
                  </div>
                  {treatment.posology && (
                    <div>
                      <span className="font-semibold">Posologie: </span>
                      <span className="text-muted-foreground">{treatment.posology}</span>
                    </div>
                  )}
                  {treatment.contraindications.length > 0 && (
                    <div>
                      <span className="font-semibold">Contre-indications: </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {treatment.contraindications.map((ci, idx) => (
                          <Badge key={idx} variant="destructive">
                            {ci}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </PageContainer>
  );
}
